import React from 'react'
import { Logic } from '~common/ui-logic/logic'
import type { AppContextType } from '~/app-context'
import { debounce } from '~common/utils/debounce'

export interface SidepanelLogicDependencies extends AppContextType {}

export interface SidepanelLogicState {
    isOpen: boolean
    content?: React.ReactNode
    width?: number
}

export class SidepanelLogic extends Logic<
    SidepanelLogicDependencies,
    SidepanelLogicState
> {
    debouncedSave?: (width: number) => void

    constructor(dependencies: SidepanelLogicDependencies) {
        super(dependencies)
        this.debouncedSave = debounce((width: number) => {
            localStorage.setItem('sidepanel-width', width.toString())
        }, 200)
    }

    getInitialState(): SidepanelLogicState {
        const savedWidthStr = localStorage.getItem('sidepanel-width')
        let width = 600
        if (savedWidthStr) {
            const parsed = parseInt(savedWidthStr, 10)
            if (!isNaN(parsed)) {
                width = parsed
            }
        }

        return {
            isOpen: false,
            content: undefined,
            width,
        }
    }

    open(content: React.ReactNode, options?: { width?: number }) {
        this.setState({
            isOpen: true,
            ...(options && { width: options.width }),
        } as Partial<SidepanelLogicState>)

        // Defer content to next tick so panel renders instantly with loader
        setTimeout(() => {
            this.setState({
                content,
            } as Partial<SidepanelLogicState>)
        }, 0)
    }

    close() {
        this.setState({
            isOpen: false,
            content: undefined,
        } as Partial<SidepanelLogicState>)
    }

    setWidth(width: number) {
        this.setState({ width })
        this.debouncedSave?.(width)
    }
}
